import { SocialService } from '../../../../../../../../services/social.service';
import { AddMembersComponent } from '../add-members/add-members.component';
import { Component, EventEmitter, Output, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from '../../../../../../../../services/toast.service';
import { LoaderComponent } from "../../../../../../reusable/loader/loader.component";
import { BackButtonComponent } from '../../../../../../../../shared/components/buttons/back-button/back-button.component';


@Component({
  selector: 'app-manage-members',
  standalone: true,
  imports: [CommonModule, FormsModule, AddMembersComponent, LoaderComponent, BackButtonComponent],
  templateUrl: './manage-members.component.html',
  styleUrl: './manage-members.component.scss'
})
export class ManageMembersComponent implements OnInit {
  @Input() fleet: any;
  @Input() readOnly = false;
  @Input() containedPreview = false;
  @Output() close = new EventEmitter<void>();
  /**
   * A member row was tapped — open that captain's profile.
   *
   * Rows used to be inert in BOTH modes, so the only thing you could do with a
   * member was remove them, and only as an owner/admin. A plain member opened
   * the roster and hit a dead end ("members list not clickable if not admin").
   * Tapping a person should take you to that person, whatever your role.
   */
  @Output() memberSelected = new EventEmitter<any>();

  showAddMembers = false;
  searchQuery = '';
  isLoading = false;
  isLoadingMore = false;
  currentPage = 1;
  pageSize = 10;
  hasMoreMembers = false;

  members: any[] = [];

  constructor(
    private socialService: SocialService,
    public toast: ToastService,
  ) { }

  ngOnInit() {
    if (this.fleet?._id) {
      this.loadMembers();
    }
  }

  get filteredMembers(): any[] {
    if (!this.searchQuery.trim()) {
      return this.sortedMembers;
    }
    const q = this.searchQuery.toLowerCase();
    return this.sortedMembers.filter(m =>
      (m.name || '').toLowerCase().includes(q)
    );
  }

  get sortedMembers(): any[] {
    return [...this.members].sort((a, b) => {
      const roleCompare = this.getRolePriority(a.role) - this.getRolePriority(b.role);
      if (roleCompare !== 0) return roleCompare;
      return String(a.name || '').localeCompare(String(b.name || ''));
    });
  }

  get totalMemberCount(): number {
    return this.members.length || Number(this.fleet?.members_count || 0);
  }

  loadMembers(page: number = 1) {
    if (page === 1) {
      this.isLoading = true;
      this.hasMoreMembers = false;
    } else {
      if (this.isLoadingMore || !this.hasMoreMembers) return;
      this.isLoadingMore = true;
    }

    this.socialService.getCommunityMembers(this.fleet._id, page, this.pageSize).subscribe({
      next: (response) => {
        const nextMembers = (response.results || []).map((item: any) => ({
          id: item._id,
          userId: item.user.user_id,
          name: item.user.name,
          username: item.user.username || item.user.name,
          image: item.user.image_url,
          role: item.role,
          vesselName: item.vessel_name || item.user?.vessel_name || item.user?.vessel || null,
        }));
        this.members = page === 1 ? nextMembers : this.mergeMembers(this.members, nextMembers);
        this.currentPage = page;
        this.hasMoreMembers = this.hasNextMembersPage(response, page);
        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error: (err: any) => {
        console.error('Error loading members:', err);
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    });
  }

  onPreviewScroll(event: Event): void {
    if (!this.readOnly || this.isLoading || this.isLoadingMore || !this.hasMoreMembers) return;

    const el = event.target as HTMLElement;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (remaining < 120) {
      this.loadMembers(this.currentPage + 1);
    }
  }

  private hasNextMembersPage(response: any, page: number): boolean {
    if (response?.paging?.next) return true;
    if (response?.next) return true;

    const totalPages = Number(response?.total_page || response?.total_pages || 0);
    if (totalPages) return page < totalPages;

    const totalCount = Number(response?.count || 0);
    return totalCount ? this.members.length < totalCount : false;
  }

  private mergeMembers(current: any[], incoming: any[]): any[] {
    const existingIds = new Set(current.map(member => member.id || member.userId));
    const uniqueIncoming = incoming.filter(member => !existingIds.has(member.id || member.userId));
    return [...current, ...uniqueIncoming];
  }

  getInitials(name: string): string {
    if (!name) return '??';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  }

  closePopup() {
    this.close.emit();
  }

  get canManageMembers(): boolean {
    if (this.readOnly) return false;
    const role = String(this.fleet?.my_role || '').toLowerCase();
    return role === 'owner' || role === 'admin';
  }

  getMemberVesselName(member: any): string {
    return String(member?.vesselName || '').trim() || 'No vessel';
  }

  getRoleLabel(role: string): string {
    const normalized = String(role || '').toLowerCase();
    if (normalized === 'owner') return 'Owner';
    if (normalized === 'admin') return 'Admin';
    return '';
  }

  private getRolePriority(role: string): number {
    const normalized = String(role || '').toLowerCase();
    if (normalized === 'owner') return 0;
    if (normalized === 'admin') return 1;
    return 2;
  }

  openAddMembers() {
    this.showAddMembers = true;
  }

  closeAddMembers() {
    this.showAddMembers = false;
  }

  onMemberAdded(_event: any) {
    // Refresh the members list instead of reloading the entire page
    this.loadMembers();
    this.showAddMembers = false;
    this.showSuccess('Invitation sent successfully');
  }

  /** Open the tapped member's profile. Available to every role. */
  onMemberClick(member: any) {
    if (!member?.userId) return;
    this.memberSelected.emit(member);
  }

  removeMember(member: any) {
    if (!this.fleet?._id || !member.userId) return;

    // Optimistic update
    const originalMembers = [...this.members];
    this.members = this.members.filter(m => m.id !== member.id);

    this.socialService.removeFromCommunity(this.fleet._id, member.userId).subscribe({
      next: () => {
        this.showSuccess('Member removed successfully');
      },
      error: (err: any) => {
        console.error('Error removing member:', err);
        this.showError('Error removing member');
        this.members = originalMembers; // Rollback
      }
    });
  }

  // ---------------- SNACKBAR HELPERS ----------------
  private showError(message: string) {
    this.toast.error(message);
  }

  private showSuccess(message: string) {
    this.toast.success(message);
  }
}
